import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from './api';
import './AdminUsers.css';

const AVATAR_PALETTE = [
  ['#4CAF50', '#2e7d32'],
  ['#3b82f6', '#1d4ed8'],
  ['#8b5cf6', '#6d28d9'],
  ['#ec4899', '#be185d'],
  ['#f59e0b', '#b45309'],
  ['#06b6d4', '#0e7490'],
  ['#ef4444', '#b91c1c'],
  ['#14b8a6', '#0f766e'],
];

function getInitials(name) {
  if (!name) return '?';
  const cleaned = name.replace(/\b(Prof|Doç|Dr|Öğr|Gör|Üyesi)\.?/gi, '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColors(seed) {
  const key = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  const idx = Math.abs(hash) % AVATAR_PALETTE.length;
  const [from, to] = AVATAR_PALETTE[idx];
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
}

function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('...');
    out.push(sorted[i]);
  }
  return out;
}

function AdminUsers({ onImpersonate }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [resettingPassword, setResettingPassword] = useState(null);
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });
  const [impersonatingUser, setImpersonatingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, page]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('type', 'instructor');
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      const response = await apiFetch(`/admin/users?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setUsers((data.users || []).slice().sort((a, b) => (a.id ?? 0) - (b.id ?? 0)));
        setTotal(data.total || 0);
      } else {
        setError(data.message || 'Akademisyenler yüklenemedi');
      }
    } catch {
      setError('Akademisyenler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Bu akademisyenin şifresini sıfırlamak istediğinize emin misiniz?')) return;
    setResettingPassword(userId);
    setResetMessage({ type: '', text: '' });
    try {
      const response = await apiFetch(`/admin/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ user_type: 'instructor' }),
      });
      const data = await response.json();
      if (data.success) {
        setResetMessage({ type: 'success', text: data.message });
        setTimeout(() => setResetMessage({ type: '', text: '' }), 5000);
      } else {
        setResetMessage({ type: 'error', text: data.message || 'Şifre sıfırlanamadı' });
      }
    } catch {
      setResetMessage({ type: 'error', text: 'Şifre sıfırlanırken bir hata oluştu' });
    } finally {
      setResettingPassword(null);
    }
  };

  const handleImpersonate = async (targetUser) => {
    if (!onImpersonate) return;
    setImpersonatingUser(targetUser.id);
    setResetMessage({ type: '', text: '' });

    try {
      const response = await apiFetch('/admin/impersonate', {
        method: 'POST',
        body: JSON.stringify({ user_id: targetUser.id, user_type: 'instructor' }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        onImpersonate({
          userType: data.user.userType,
          user: data.user,
          token: data.token,
          impersonation: data.impersonation,
        });
      } else {
        setResetMessage({ type: 'error', text: data.message || 'Hesaba giriş yapılamadı' });
      }
    } catch {
      setResetMessage({ type: 'error', text: 'Hesaba geçiş sırasında bir hata oluştu' });
    } finally {
      setImpersonatingUser(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pageList = useMemo(() => buildPageList(page, totalPages), [page, totalPages]);
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(total, page * limit);

  return (
    <div className="au-page">
      <div className="au-toolbar">
        <div className="au-toolbar-top">
          <div className="au-title-block">
            <h2 className="au-title">Akademisyenler</h2>
            <p className="au-subtitle">Akademisyen hesaplarını yönetin, şifre sıfırlayın veya hesaplara geçiş yapın.</p>
          </div>
          <div className="au-count-pill">
            <span className="au-count-pill-dot" />
            {total} kayıtlı akademisyen
          </div>
        </div>

        <div className="au-search-wrapper">
          <span className="au-search-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="text"
            className="au-search-input"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            placeholder="Ad, soyad veya e-posta ile ara..."
          />
          {searchTerm && (
            <button
              type="button"
              className="au-search-clear"
              onClick={() => { setSearchTerm(''); setPage(1); }}
              aria-label="Aramayı temizle"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {resetMessage.text && (
        <div className={`au-flash ${resetMessage.type}`}>
          {resetMessage.type === 'success' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
          {resetMessage.text}
        </div>
      )}
      {error && (
        <div className="au-flash error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      <div className="au-card">
        <div className="au-table-wrap">
          {loading ? (
            <div className="au-skeleton">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="au-skeleton-row">
                  <div className="au-skeleton-block" style={{ width: 38, height: 22 }} />
                  <div className="au-skeleton-circle" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className="au-skeleton-block" style={{ width: '40%' }} />
                    <div className="au-skeleton-block" style={{ width: '60%', height: 11 }} />
                  </div>
                  <div className="au-skeleton-block" style={{ width: 180, height: 32 }} />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="au-empty">
              <div className="au-empty-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="au-empty-text">Akademisyen bulunamadı</div>
              {searchTerm && <div className="au-empty-sub">"{searchTerm}" için sonuç yok. Aramayı değiştirmeyi deneyin.</div>}
            </div>
          ) : (
            <table className="au-table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ID</th>
                  <th>Akademisyen</th>
                  <th>E-posta</th>
                  <th className="right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <span className="au-id-badge">#{user.id}</span>
                    </td>
                    <td>
                      <div className="au-user-cell">
                        <div className="au-avatar" style={{ background: getAvatarColors(user.id) }}>
                          {getInitials(user.name)}
                        </div>
                        <div className="au-user-info">
                          <span className="au-user-name">{user.name || '-'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="au-email-cell">{user.email || '-'}</td>
                    <td>
                      <div className="au-actions">
                        <button
                          onClick={() => handleImpersonate(user)}
                          disabled={impersonatingUser === user.id}
                          className="au-btn au-btn-impersonate"
                          title="Bu akademisyenin hesabına geçiş yap"
                        >
                          {impersonatingUser === user.id ? (
                            <>
                              <span className="au-btn-spin" />
                              <span className="au-btn-text">Açılıyor</span>
                            </>
                          ) : (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                <polyline points="10 17 15 12 10 7" />
                                <line x1="15" y1="12" x2="3" y2="12" />
                              </svg>
                              <span className="au-btn-text">Hesaba Gir</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleResetPassword(user.id)}
                          disabled={resettingPassword === user.id}
                          className="au-btn au-btn-reset"
                          title="Şifreyi sıfırla"
                        >
                          {resettingPassword === user.id ? (
                            <>
                              <span className="au-btn-spin" />
                              <span className="au-btn-text">Sıfırlanıyor</span>
                            </>
                          ) : (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 2v6h-6" />
                                <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                                <path d="M3 22v-6h6" />
                                <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                              </svg>
                              <span className="au-btn-text">Şifre Sıfırla</span>
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && users.length > 0 && totalPages > 1 && (
          <div className="au-pagination">
            <div className="au-pagination-info">
              <strong>{rangeStart}–{rangeEnd}</strong> / {total} akademisyen
            </div>
            <div className="au-pagination-controls">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="au-page-btn"
                aria-label="Önceki sayfa"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              {pageList.map((p, idx) =>
                p === '...' ? (
                  <span key={`e${idx}`} className="au-page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`au-page-btn ${p === page ? 'active' : ''}`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="au-page-btn"
                aria-label="Sonraki sayfa"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsers;
